import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import Search from "../Atomos/Icons/Search";
import EyeOpen from "../Atomos/Icons/EyeOpen";
import EyeClosed from "../Atomos/Icons/EyeClosed";
import { staffFieldRoot, staffFieldShell } from "./fieldStyles";

type SupportedInputType = "text" | "password" | "number" | "search";

interface InputTextBaseProps {
  title?: string;
  name?: string;
  id?: string;
  required?: boolean;
  onChange?: (value: string) => void;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  tooltip?: string;
  type?: SupportedInputType | string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  className?: string;
  inputClassName?: string;
  autoComplete?: string;
  min?: number;
  max?: number;
  step?: number;
  readOnly?: boolean;
  showAsLabel?: boolean;
  onBlur?: () => void;
  spellCheck?: boolean;
  lpIgnore?: boolean;
}

type InputTextProps = InputTextBaseProps;

const InputText = ({
  title,
  name,
  id,
  required = false,
  onChange,
  value,
  placeholder,
  disabled = false,
  error = false,
  errorMessage,
  tooltip,
  type = "text",
  prefix,
  suffix,
  className = "",
  inputClassName = "",
  autoComplete,
  min,
  max,
  step,
  readOnly = false,
  showAsLabel = false,
  onBlur,
  spellCheck,
  lpIgnore,
}: InputTextProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const safeType = useMemo<SupportedInputType>(() => {
    const normalizedType = String(type).toLowerCase();
    if (
      normalizedType === "text" ||
      normalizedType === "password" ||
      normalizedType === "number" ||
      normalizedType === "search"
    ) {
      return normalizedType;
    }
    return "text";
  }, [type]);

  const inputType =
    safeType === "password" ? (showPassword ? "text" : "password") : safeType;

  const renderAsLabel = readOnly || (showAsLabel && disabled);

  // React's onWheel is passive in modern browsers, so preventDefault is ignored.
  // Native non-passive listener is required to block scroll-changing number values.
  useEffect(() => {
    if (safeType !== "number" || renderAsLabel) {
      return;
    }
    const el = inputRef.current;
    if (!el) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
    };
  }, [renderAsLabel, safeType]);

  return (
    <div className={`${staffFieldRoot} ${className}`}>
      {title && (
        <label
          htmlFor={renderAsLabel ? undefined : id ?? name}
          title={tooltip}
          className="font-semibold text-gray-font"
        >
          {title}
          {required && <span className="text-primary"> *</span>}
        </label>
      )}
      {renderAsLabel ? (
        <p className="flex items-center gap-1 text-gray-font">
          {prefix && (
            <span className="shrink-0 text-sm leading-5">{prefix}</span>
          )}
          <span>{value?.trim() ? value : "-"}</span>
          {suffix && (
            <span className="shrink-0 text-sm leading-5">{suffix}</span>
          )}
        </p>
      ) : (
        <div
          className={`${staffFieldShell} ${error
            ? "bg-gray-25 text-primary outline outline-2 outline-primary"
            : disabled
              ? "cursor-not-allowed bg-gray-100 text-gray-400 outline outline-2 outline-gray-200"
              : "bg-gray-25 text-input-gray"
            }`}
        >
          <div className="flex w-full items-center gap-2">
            {safeType === "search" && (
              <span className="pointer-events-none h-5 w-5 shrink-0 text-gray-font">
                <Search />
              </span>
            )}

            {prefix && (
              <span className={`shrink-0 text-sm leading-5 ${disabled ? 'text-gray-400' : 'text-gray-font'} `}>{prefix}</span>
            )}

            <input
              ref={inputRef}
              id={id ?? name}
              name={name}
              placeholder={placeholder}
              disabled={disabled}
              type={inputType}
              min={safeType === "number" ? min : undefined}
              max={safeType === "number" ? max : undefined}
              step={safeType === "number" ? step : undefined}
              autoComplete={autoComplete}
              spellCheck={spellCheck}
              {...(lpIgnore ? ({ "data-lpignore": "true" } as const) : {})}
              value={value}
              onChange={(event) => onChange?.(event.target.value)}
              onBlur={() => onBlur?.()}
              required={required}
              className={`staff-input-text-inner !m-0 !block !h-5 !w-full !min-w-0 !border-0 !outline-none !bg-transparent !p-0 !text-sm !font-normal !leading-5 text-gray-font placeholder:text-gray-400 focus:!outline-none focus:!ring-0 disabled:cursor-not-allowed disabled:!bg-transparent disabled:text-gray-400 ${inputClassName}`}
            />

            {suffix && (
              <span className={`shrink-0 text-sm leading-5 ${disabled ? 'text-gray-400' : 'text-gray-font'} `}>{suffix}</span>
            )}

            {safeType === "password" && (
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={disabled}
                className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-gray-font transition-colors hover:text-secondary disabled:cursor-not-allowed disabled:text-gray-300"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeClosed /> : <EyeOpen />}
              </button>
            )}
          </div>
        </div>
      )}

      {error && errorMessage && !renderAsLabel && (
        <span className="text-primary">{errorMessage}</span>
      )}
    </div>
  );
};

export default InputText;
